import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, Download } from "lucide-react";

import { useModalContext } from "../../components/ModalProvider";
import { AttributeGap, ClassGap, useDocumentAnalysisAPI } from "../../DocumentAnalysisAPI";
import { classLabel, isKnownClass } from "./ontologyValidation";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

/**
 * What the documents said that the model cannot hold.
 *
 * Nothing is collected for this screen. Every run has been recording what it could not place since
 * the extraction step was built — an attribute the class does not declare, a term the catalogue has
 * no class for — and this asks across runs how often each occurred and with what values. That is
 * the difference between a curiosity and a decision: one document naming a `representative` is an
 * oddity, four of them across two runs is a field the model wants.
 *
 * Two kinds, kept apart because they are cured differently. An attribute gap is a class that exists
 * and a property it lacks — the cure is a field. A class gap is a term with no class at all — the
 * cure is a class, or the finding that the term belongs on an existing one.
 *
 * The example values are what makes the list usable: a name for a field can be chosen without them,
 * a type cannot.
 */

/**
 * Wofür eine Lücke steht — die jüngste Lesart sichtbar, die älteren im Kurztext.
 *
 * Die Extraktion schreibt zu jeder Lücke einen Satz: was das Feld bedeutet, was die Sache ist. Über
 * mehrere Läufe kommen mehrere zusammen, und sie sind nicht austauschbar — ein späterer Lauf
 * formuliert oft genauer. Angezeigt wird deshalb der jüngste; die übrigen stehen darunter, weil die
 * eine Beschreibung, die später am Feld im Modell steht, mit den Belegen vor Augen gewählt werden
 * soll und nicht von dem Lauf, der zufällig der letzte war.
 */
function GapDescription({ descriptions }: { descriptions?: string[] }) {
  const { t } = useTranslation();
  const list = descriptions ?? [];

  if (list.length === 0) {
    return (
      <TableCell className="max-w-[320px] text-xs text-muted-foreground">
        {t("documentAnalysisPanel.gaps.noDescription")}
      </TableCell>
    );
  }

  const [newest, ...older] = list;

  return (
    <TableCell className="max-w-[320px] text-xs" title={list.join("\n\n")}>
      <span>{newest}</span>
      {older.length > 0 ? (
        <span className="mt-0.5 block text-muted-foreground">
          {t("documentAnalysisPanel.gaps.moreReadings", { count: older.length })}
        </span>
      ) : null}
    </TableCell>
  );
}

interface Properties {}

export default function GapsTab(_properties: Properties) {
  const { t } = useTranslation();
  const { pushError } = useModalContext() as any;
  const api = useDocumentAnalysisAPI();

  const [attributeGaps, setAttributeGaps] = useState<AttributeGap[]>([]);
  const [classGaps, setClassGaps] = useState<ClassGap[]>([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [minOccurrences, setMinOccurrences] = useState(1);

  /**
   * How the list is read, which is not one question.
   *
   * By frequency to decide what to do first — the default, because curation starts with what keeps
   * happening. By class to work a class through in one go: adding three fields to `Lease` is one
   * decision about one class, and hunting its rows out of a frequency-ordered list is needless work.
   */
  const [sort, setSort] = useState<{ column: string; direction: "asc" | "desc" }>({
    column: "occurrences",
    direction: "desc",
  });

  const sortBy = (column: string) =>
    setSort((current) => ({
      column,
      // A first click on a new column sorts the way that column is usually read: names upwards,
      // counts and dates with the largest first.
      direction:
        current.column === column
          ? current.direction === "asc"
            ? "desc"
            : "asc"
          : column === "occurrences" || column === "lastSeen"
            ? "desc"
            : "asc",
    }));

  const header = (column: string, label: string) => (
    <TableHead className="whitespace-nowrap">
      <button
        type="button"
        className="inline-flex items-center gap-1 font-medium hover:underline"
        onClick={() => sortBy(column)}
      >
        {label}
        {sort.column === column ? (
          sort.direction === "asc" ? (
            <ArrowUp className="h-3 w-3" aria-hidden />
          ) : (
            <ArrowDown className="h-3 w-3" aria-hidden />
          )
        ) : null}
      </button>
    </TableHead>
  );

  /**
   * Sorted in the browser, not by the server.
   *
   * The whole filtered list is already here — the query is capped at 500 rows, and a curation list
   * longer than that is not one anybody reads through. Asking the server again to reorder what is
   * on screen would cost a round trip for nothing.
   */
  const sorted = <T extends Record<string, any>>(rows: T[], tiebreak: string): T[] => {
    const direction = sort.direction === "asc" ? 1 : -1;

    const compare = (one: any, other: any): number => {
      if (typeof one === "number" && typeof other === "number") return one - other;

      // A gap never seen sorts last whichever way the column points; "missing" is not "earliest".
      if (!one && !other) return 0;
      if (!one) return 1;
      if (!other) return -1;

      return String(one).localeCompare(String(other), "de");
    };

    return [...rows].sort((left, right) => {
      const primary = compare(left[sort.column], right[sort.column]) * direction;
      if (primary !== 0) return primary;

      // Within a class, the frequent gaps first: sorting by class is how a whole class is worked
      // through in one go, and there the order that matters is which of its gaps to close first.
      const byFrequency = (right.occurrences ?? 0) - (left.occurrences ?? 0);
      if (byFrequency !== 0) return byFrequency;

      return compare(left[tiebreak], right[tiebreak]);
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const attributeRows = useMemo(() => sorted(attributeGaps, "attribute"), [attributeGaps, sort]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const classRows = useMemo(() => sorted(classGaps, "term"), [classGaps, sort]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.getGaps({ q: search || undefined, minOccurrences });
      setAttributeGaps(result.attributeGaps ?? []);
      setClassGaps(result.classGaps ?? []);
    } catch (error) {
      pushError(error);
    } finally {
      setLoading(false);
    }
    // The API object is rebuilt on every render; its behaviour is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, minOccurrences]);

  useEffect(() => {
    void load();
  }, [load]);

  /** The list as it would be worked through — taken away to be turned into model changes. */
  const download = () => {
    const url = URL.createObjectURL(
      new Blob([JSON.stringify({ attributeGaps, classGaps }, null, 2)], { type: "application/json" }),
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = "ontologie-luecken.json";
    link.click();
    URL.revokeObjectURL(url);
  };

  const seen = (value: string | null) =>
    value ? new Date(value).toLocaleDateString("de-DE", { dateStyle: "short" }) : "—";

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <p className="max-w-prose text-sm text-muted-foreground">{t("documentAnalysisPanel.gaps.intro")}</p>

      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[220px] flex-1">
          <Label htmlFor="gapSearch">{t("documentAnalysisPanel.gaps.search")}</Label>
          <Input
            id="gapSearch"
            value={search}
            placeholder={t("documentAnalysisPanel.gaps.searchPlaceholder")}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        <div>
          <Label htmlFor="gapMinimum">{t("documentAnalysisPanel.gaps.minOccurrences")}</Label>
          <Input
            id="gapMinimum"
            type="number"
            min={1}
            className="w-[120px]"
            value={minOccurrences}
            onChange={(event) => setMinOccurrences(Math.max(parseInt(event.target.value, 10) || 1, 1))}
          />
        </div>

        <div className="flex-1" />

        <Button variant="outline" onClick={download}>
          <Download className="mr-2 h-4 w-4" aria-hidden />
          {t("documentAnalysisPanel.gaps.download")}
        </Button>
      </div>

      {loading ? (
        <div className="flex flex-col gap-2">
          {[0, 1, 2, 3].map((row) => (
            <Skeleton key={row} className="h-10 w-full" />
          ))}
        </div>
      ) : (
        <Tabs defaultValue="attributes">
          <TabsList>
            <TabsTrigger value="attributes">
              {t("documentAnalysisPanel.gaps.attributeTab", { count: attributeGaps.length })}
            </TabsTrigger>
            <TabsTrigger value="classes">
              {t("documentAnalysisPanel.gaps.classTab", { count: classGaps.length })}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="attributes" className="mt-3">
            <p className="mb-2 text-xs text-muted-foreground">
              {t("documentAnalysisPanel.gaps.attributeExplanation")}
            </p>

            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {header("className", t("documentAnalysisPanel.gaps.class"))}
                    {header("attribute", t("documentAnalysisPanel.gaps.attribute"))}
                    {header("occurrences", t("documentAnalysisPanel.gaps.occurrences"))}
                    <TableHead>{t("documentAnalysisPanel.gaps.terms")}</TableHead>
                    <TableHead>{t("documentAnalysisPanel.gaps.description")}</TableHead>
                    <TableHead>{t("documentAnalysisPanel.gaps.examples")}</TableHead>
                    {header("lastSeen", t("documentAnalysisPanel.gaps.lastSeen"))}
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {attributeRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="py-8 text-center text-sm text-muted-foreground">
                        {t("documentAnalysisPanel.gaps.none")}
                      </TableCell>
                    </TableRow>
                  ) : (
                    attributeRows.map((gap) => (
                      <TableRow key={`${gap.className}.${gap.attribute}`} className="align-top">
                        <TableCell className="whitespace-nowrap text-sm">
                          {/* Both, and here on purpose: this screen is about the model, so the
                              identifier is the thing being decided about — with the label beside it
                              so the reader knows what kind of thing it is. */}
                          <span className="font-mono">{gap.className}</span>
                          {isKnownClass(gap.className) ? (
                            <span className="ml-1 text-xs text-muted-foreground">
                              {classLabel(gap.className)}
                            </span>
                          ) : null}
                        </TableCell>

                        <TableCell className="whitespace-nowrap font-mono text-sm">{gap.attribute}</TableCell>

                        <TableCell className="whitespace-nowrap text-sm">
                          {/* Occurrences and runs apart: one document repeating itself is not
                              several documents agreeing, and only the second is evidence. */}
                          {gap.occurrences}
                          <span className="text-muted-foreground">
                            {" "}
                            / {t("documentAnalysisPanel.gaps.inRuns", { count: gap.analyses })}
                          </span>
                        </TableCell>

                        <TableCell className="text-xs">{gap.terms.join(", ")}</TableCell>

                        <GapDescription descriptions={gap.descriptions} />

                        <TableCell className="max-w-[380px] text-xs text-muted-foreground">
                          {gap.examples.join(" · ")}
                        </TableCell>

                        <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                          {seen(gap.lastSeen)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="classes" className="mt-3">
            <p className="mb-2 text-xs text-muted-foreground">
              {t("documentAnalysisPanel.gaps.classExplanation")}
            </p>

            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {header("term", t("documentAnalysisPanel.gaps.term"))}
                    {header("occurrences", t("documentAnalysisPanel.gaps.occurrences"))}
                    <TableHead>{t("documentAnalysisPanel.gaps.roles")}</TableHead>
                    <TableHead>{t("documentAnalysisPanel.gaps.proposedFields")}</TableHead>
                    <TableHead>{t("documentAnalysisPanel.gaps.description")}</TableHead>
                    <TableHead>{t("documentAnalysisPanel.gaps.examples")}</TableHead>
                    {header("lastSeen", t("documentAnalysisPanel.gaps.lastSeen"))}
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {classRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="py-8 text-center text-sm text-muted-foreground">
                        {t("documentAnalysisPanel.gaps.none")}
                      </TableCell>
                    </TableRow>
                  ) : (
                    classRows.map((gap) => (
                      <TableRow key={gap.term} className="align-top">
                        <TableCell className="whitespace-nowrap text-sm font-medium">{gap.term}</TableCell>

                        <TableCell className="whitespace-nowrap text-sm">
                          {gap.occurrences}
                          <span className="text-muted-foreground">
                            {" "}
                            / {t("documentAnalysisPanel.gaps.inRuns", { count: gap.analyses })}
                          </span>
                        </TableCell>

                        <TableCell className="text-xs">{gap.roles.join(", ")}</TableCell>

                        {/* What the extraction named its own fields — a proposal for what the class
                            would hold, written by the documents rather than at a whiteboard. */}
                        <TableCell className="max-w-[320px] font-mono text-xs">
                          {gap.attributes.join(", ")}
                        </TableCell>

                        <GapDescription descriptions={gap.descriptions} />

                        <TableCell className="max-w-[320px] text-xs text-muted-foreground">
                          {gap.examples.join(" · ")}
                        </TableCell>

                        <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                          {seen(gap.lastSeen)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
